import type { Dispatch, SetStateAction } from 'react'
import { useCallback, useState } from 'react'

interface UseListSelectionReturn {
  readonly selectedIds: ReadonlySet<string>
  readonly confirmingId: string | null
  readonly setSelectedIds: Dispatch<SetStateAction<ReadonlySet<string>>>
  readonly setConfirmingId: Dispatch<SetStateAction<string | null>>
  readonly handleSelectOne: (id: string) => void
  readonly handleRequestConfirm: (id: string) => void
  readonly handleCancelConfirm: () => void
  readonly resetSelection: () => void
}

export function useListSelection(): UseListSelectionReturn {
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(new Set<string>())
  const [confirmingId, setConfirmingId] = useState<string | null>(null)

  const handleSelectOne = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }, [])

  const handleRequestConfirm = useCallback((id: string) => {
    setConfirmingId(id)
  }, [])

  const handleCancelConfirm = useCallback(() => {
    setConfirmingId(null)
  }, [])

  const resetSelection = useCallback(() => {
    setSelectedIds(new Set<string>())
    setConfirmingId(null)
  }, [])

  return {
    selectedIds,
    confirmingId,
    setSelectedIds,
    setConfirmingId,
    handleSelectOne,
    handleRequestConfirm,
    handleCancelConfirm,
    resetSelection,
  }
}
